import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal from "molecules/modals/modal";
import PublicationPage from "pages/publications/publication-page";
import React from "react";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import { useLocalization } from "utilities/hooks/use-localization";
import useModalActions from "utilities/hooks/use-modal-actions";
import MobileTableOfContentsProvider from "./mobile-table-of-contents-context";
import PdfTableOfContentsWithMeta from "./pdf-table-of-contents-with-meta";
import LegacyPublicationRecord from "models/view-models/legacy-publication-record";
import FreeAccessPublicationRecord from "models/view-models/free-access-publication-record";
import { atom, useAtomValue, useSetAtom } from "jotai";
import PdfTableOfContentsEntry from "models/interfaces/pdf-table-of-contents-entry";
import ImagePage from "models/interfaces/image-page";
import PublishStatus from "models/enumerations/publish-status";

interface MobileTableOfContentsButtonProps {
    cssClassName?: string;
}

const MobileTableOfContentsButton = ({
    cssClassName,
}: MobileTableOfContentsButtonProps) => {
    const { publication, loaded, errors } = useCurrentPublication();
    const pdfPublication = useCurrentPdfPublication();
    const { isOpen, handleClose, handleOpen } = useModalActions();
    const { t } = useLocalization();

    return (
        <>
            <div className={cssClassName}>
                <button
                    type="button"
                    className="-button"
                    name={t("openNavigation")}
                    aria-label={t("openNavigation")}
                    onClick={handleOpen}>
                    <Icon type={Icons.MetaPanel} size={IconSizes.Large} />
                </button>
            </div>

            <Modal
                closeDialog={handleClose}
                isVisible={isOpen}
                label={t("tableOfContents")}
                type={ModalTypes.Bottom}
                closeButtonStyle={ModalCloseButtonStyle.Hidden}
                cssClassName="c-mobile-table-of-contents"
                transition={ModalTransitions.None}>
                <MobileTableOfContentsProvider onClick={handleClose}>
                    {pdfPublication != null ? (
                        <PdfTableOfContentsWithMeta
                            publication={pdfPublication.publication}
                            publicationLoaded={true}
                            pageErrors={errors}
                            onBackClick={handleClose}
                            tableOfContentsEntries={
                                pdfPublication.tableOfContentsEntries
                            }
                            pageEntries={pdfPublication.pageEntries}
                            code={pdfPublication.code}
                            edition={pdfPublication.edition}
                            title={pdfPublication.title}
                            isPublished={pdfPublication.isPublished}
                        />
                    ) : (
                        <PublicationPage
                            publication={publication}
                            publicationLoaded={loaded}
                            pageErrors={errors}
                            type="back"
                            onBackClick={handleClose}
                        />
                    )}
                </MobileTableOfContentsProvider>
            </Modal>
        </>
    );
};

export default MobileTableOfContentsButton;

const currentPdfPublicationAtom = atom<
    LegacyPublicationRecord | FreeAccessPublicationRecord | null
>(null);

type PdfPublication = {
    publication?: LegacyPublicationRecord;
    code: string;
    edition: string;
    title: string;
    tableOfContentsEntries: PdfTableOfContentsEntry[];
    pageEntries: ImagePage[];
    isPublished: boolean;
};

export const useCurrentPdfPublication = () => {
    const publication = useAtomValue(currentPdfPublicationAtom);
    if (publication == null) {
        return null;
    }

    const info = {
        code: publication?.code ?? "",
        edition: publication?.edition ?? "",
        title: publication?.title ?? "",
    };

    const pdfPublication: PdfPublication =
        publication instanceof LegacyPublicationRecord
            ? {
                  publication,
                  tableOfContentsEntries: publication.tableOfContents,
                  pageEntries: publication.legacyPublicationPages ?? [],
                  isPublished:
                      publication.publishStatus === PublishStatus.Published,
                  ...info,
              }
            : {
                  tableOfContentsEntries:
                      publication?.freeAccessPublicationTableOfContents ?? [],
                  pageEntries: publication?.freeAccessPublicationPages ?? [],
                  isPublished: publication.isPublished ?? false,
                  ...info,
              };

    return pdfPublication;
};

export const useSetCurrentPdfPublication = () => {
    const setPublication = useSetAtom(currentPdfPublicationAtom);

    return { setPublication };
};
